import { api } from '@ppwr/convex/_generated/api'
import type { LwCourseProgressSnapshot } from '@ppwr/convex/learnworlds/types'
import { useAction } from 'convex/react'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ActivityIndicator, Linking, StyleSheet, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { WebView } from 'react-native-webview'

import { MainHeader } from '../../components/MainHeader/MainHeader'
import { DS } from '../../design-system'

/**
 * Hosts the player itself is allowed to navigate to. Everything else — a marketing
 * link, a support page — is left to the system browser.
 *
 * This is a navigation rule, not an asset rule: `originWhitelist` is left at `*` because
 * the player pulls styles, fonts and media from several LearnWorlds CDNs, and narrowing
 * it renders the course unstyled.
 */
const ALLOWED_HOSTS = [
  'learn.pokerpower.com',
  'learnworlds.com',
  'mycourse.app',
  // Video units are hosted off-school. Android reports every request as top-frame
  // (`isTopFrame` is hardcoded `true` there), so the iframe escape hatch below never
  // fires and an embedded player would otherwise be pushed out to the system browser.
  'youtube.com',
  'youtube-nocookie.com',
  'ytimg.com',
  'vimeo.com',
  'wistia.com',
  'wistia.net'
]

const isAllowed = (url: string) => {
  // RN's `URL` is a partial polyfill, so pull the host out by hand.
  const hostname = url.replace(/^[a-z]+:\/\//i, '').split(/[/?#]/)[0]
  return ALLOWED_HOSTS.some((host) => hostname === host || hostname.endsWith(`.${host}`))
}

/**
 * Styles layered over the LearnWorlds path player. Injected as a `<style>` tag before the
 * page renders, so nothing flashes, and it keeps applying as the player swaps units
 * client-side. The colour classes on the topbar are left out of the selector: they
 * follow the school theme.
 */
const PLAYER_CSS = `
  /* The arrow tab that expands the left path bar; the app has its own navigation. */
  #wrapper > .-first-col.collapse > .-first-col-topbar > div { display: none !important; }
`

// Runs before `<head>` is parsed. Writing into `<html>` that early leaves the player
// unstyled, so wait for `<head>` to exist and add the style there.
const INJECT_PLAYER_CSS = `(function () {
  function inject() {
    if (!document.head) return false;
    var style = document.createElement('style');
    style.textContent = ${JSON.stringify(PLAYER_CSS)};
    document.head.appendChild(style);
    return true;
  }
  if (inject()) return;
  var observer = new MutationObserver(function () {
    if (inject()) observer.disconnect();
  });
  observer.observe(document, { childList: true, subtree: true });
})();
true;`

/**
 * Opens a LearnWorlds course in a WebView, signed in as the current user.
 *
 * The SSO url is single-use, so it is minted on every mount and a retry re-mints
 * rather than reloading.
 */
export const CoursePlayerScreen = () => {
  const router = useRouter()
  const { slug, unitId, title } = useLocalSearchParams<{ slug: string; unitId?: string; title?: string }>()
  const getPlayerUrl = useAction(api.learnworlds.actions.getPlayerUrl)
  const trackCourseProgress = useAction(api.learnworlds.actions.trackCourseProgress)
  const webview = useRef<WebView>(null)
  // Progress when the player opened; diffed server-side on close to emit course events.
  const baseline = useRef<{ lwUserId: string; progress: LwCourseProgressSnapshot | null } | null>(null)

  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!slug) return
    setError(null)
    setUrl(null)
    getPlayerUrl({ slug, unitId })
      .then(({ url, lwUserId, progress }) => {
        // A retry re-mints the url but keeps the first baseline, so nothing done in
        // between is lost from the diff.
        if (lwUserId && !baseline.current) baseline.current = { lwUserId, progress }
        setUrl(url)
      })
      .catch((e: Error) => setError(e.message))
  }, [getPlayerUrl, slug, unitId])

  useEffect(() => {
    load()
  }, [load])

  useEffect(
    () => () => {
      if (!(slug && baseline.current)) return
      const { lwUserId, progress } = baseline.current
      trackCourseProgress({ slug, lwUserId, before: progress }).catch((e: Error) =>
        console.warn('[courses] trackCourseProgress failed', e.message)
      )
    },
    [slug, trackCourseProgress]
  )

  return (
    <SafeAreaView style={styles.container}>
      <MainHeader
        leftPress={() => router.back()}
        title={title || 'Course'}
        titleStyle={{ ...DS.typography['Heading 2/Regular'], marginLeft: DS.spacing[12] }}
      />

      {error ? (
        <View style={styles.centered}>
          <DS.Text variant="Body Small/Regular" color="Utility/Error" textAlign="center">
            {error}
          </DS.Text>
          <TouchableOpacity onPress={load}>
            <DS.Text variant="Body Small/Medium">Try again</DS.Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {!(url || error) ? <ActivityIndicator style={styles.centered} /> : null}

      {url ? (
        <WebView
          ref={webview}
          source={{ uri: url }}
          originWhitelist={['*']}
          sharedCookiesEnabled
          thirdPartyCookiesEnabled
          startInLoadingState
          injectedJavaScriptBeforeContentLoaded={INJECT_PLAYER_CSS}
          // Video units do not play without these. All three default to the
          // playback-hostile value: WKWebView refuses to start an inline `<video>`
          // unless `allowsInlineMediaPlayback` is set, Android's fullscreen button is
          // inert without `allowsFullscreenVideo`, and the player's own `play()` call
          // counts as autoplay, so it is blocked while a user gesture is required.
          allowsInlineMediaPlayback
          allowsFullscreenVideo
          mediaPlaybackRequiresUserAction={false}
          onShouldStartLoadWithRequest={(request) => {
            // Course units embed YouTube and other players in iframes; only a top-frame
            // navigation off LearnWorlds should leave the app.
            if (request.isTopFrame === false) return true
            if (isAllowed(request.url)) return true
            Linking.openURL(request.url)
            return false
          }}
          onError={() => setError('Could not load the course. Please try again.')}
        />
      ) : null}
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: DS.palette['Brand/Cream']
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: DS.spacing[12],
    padding: DS.spacing[24]
  }
})
